import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';
import {
  Autocomplete, TextField, CircularProgress, Box, Typography, Paper, Stack,
  Avatar, Button, Alert, Chip
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { debounce } from 'lodash';
import RegistrarEntradaModal from './RegistrarEntradaModal';

function ConsultaAcessoLiberado() {
  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovel, setSelectedImovel] = useState(null);

  const [acessos, setAcessos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAcesso, setSelectedAcesso] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
    setLoadingImoveis(true);
    api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
      .then((response) => setImovelOptions(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar imoveis:', error))
      .finally(() => setLoadingImoveis(false));
  }, 500), []);

  useEffect(() => () => debouncedFetchImoveis.cancel(), [debouncedFetchImoveis]);

  const fetchAcessos = (imovelId) => {
    if (!imovelId) {
      setAcessos([]);
      return;
    }
    setLoading(true);
    setErrorMsg('');
    api.get(`/acessos-liberados/consulta?imovelId=${imovelId}`)
      .then((response) => setAcessos(response.data.data || []))
      .catch((error) => {
        console.error('Erro ao consultar acessos liberados:', error);
        setErrorMsg('Erro ao consultar acessos liberados.');
      })
      .finally(() => setLoading(false));
  };

  const handleRegistrar = (acesso) => {
    setSelectedAcesso(acesso);
    setModalOpen(true);
  };

  const handleRegistered = () => {
    setSuccessMsg('Entrada registrada com sucesso.');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Consulta Rápida por Unidade</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Informe o número da unidade para ver as pessoas com acesso liberado vigente hoje.
      </Typography>

      <Autocomplete
        options={imovelOptions}
        getOptionLabel={(option) => `${option.numero} - ${option.descricao}`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedImovel}
        onChange={(event, newValue) => {
          setSelectedImovel(newValue);
          fetchAcessos(newValue ? newValue.id : null);
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'input') {
            if (newInputValue) debouncedFetchImoveis(newInputValue);
            else setImovelOptions([]);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Unidade / Casa"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {successMsg && <Alert severity="success" sx={{ mt: 2 }}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mt: 2 }}>{errorMsg}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : selectedImovel && acessos.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 3 }}>
          Nenhuma pessoa com acesso liberado vigente para esta unidade.
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mt: 3 }}>
          {acessos.map((acesso) => (
            <Paper key={acesso.id} sx={{ p: 2 }} variant="outlined">
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar src={acesso.foto_base64 || undefined} sx={{ width: 56, height: 56 }}>
                  {(acesso.pessoa_nome || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {acesso.pessoa_nome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documento: {acesso.pessoa_cpf || acesso.pessoa_rg || 'Não informado'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Autorizador: {acesso.autorizador_nome || 'N/A'}
                  </Typography>
                </Box>
                <Chip size="small" color="success" label="Acesso Liberado" sx={{ mr: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => handleRegistrar(acesso)}
                >
                  Registrar Entrada
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <RegistrarEntradaModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        acessoLiberado={selectedAcesso}
        onRegistered={handleRegistered}
      />
    </Box>
  );
}

export default ConsultaAcessoLiberado;
